import React, { useEffect, useState } from "react";
import axios from "axios";
import backIcon from "../icons/back_icn.png";
import styles from "./AfterPayment.module.css";

const AfterPayment = ({ status, onBack }) => {
  const [currentStatus, setCurrentStatus] = useState(status);
  const [loading, setLoading] = useState(!status);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [fullName, setFullName] = useState("");
  const [licenseGenerated, setLicenseGenerated] = useState(false);
  const [licenseUrl, setLicenseUrl] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    setIsAuthenticated(!!token);
  }, []);

  useEffect(() => {
    if (!status && isAuthenticated) {
      const fetchStatus = async () => {
        const params = new URLSearchParams(window.location.search);
        const fetchedOrderId = params.get('orderId');
        if (fetchedOrderId) {
          setOrderId(fetchedOrderId);
          try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`/api/payments/status?orderId=${encodeURIComponent(fetchedOrderId)}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            setCurrentStatus(response.data.status);
            if (response.data.downloadUrl) {
              setDownloadUrl(response.data.downloadUrl);
            }

            window.history.replaceState(null, '', window.location.pathname);
          } catch (error) {
            setCurrentStatus('error');
            window.history.replaceState(null, '', window.location.pathname);
          } finally {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      };

      fetchStatus();
    } else {
      setLoading(false);
    }
  }, [status, isAuthenticated]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
        setMessageType(null);
      }, 3500);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleGenerateLicense = async () => {
    if (!fullName.trim()) {
      setMessage({ text: "Hey, drop your full name here!", type: "error" });
      return;
    }

    if (!orderId) {
      setMessage({ text: "Couldn't figure out your order. Hit up support for help!", type: "error" });
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post('/api/licenses/generate-license', {
        orderId,
        fullName,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.licenseUrl) {
        setLicenseUrl(response.data.licenseUrl);
        setLicenseGenerated(true);
	setMessage({ text: "License generated successfully!", type: "success" });
      } else {
        setMessage({ text: "Couldn't generate the license. Try again later!", type: "error" });
      }
    } catch (error) {
      setMessage({ text: "Something went wrong while generating the license. Try again later!", type: "error" });
    }
  };

  const handleDownloadLicense = async () => {
    if (!licenseUrl) {
      setMessage({ text: "License URL is not available.", type: "error" });
      return;
    }

    try {
      setDownloadProgress(0);
      const token = localStorage.getItem('authToken');

      const response = await axios.get(licenseUrl, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
	onDownloadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setDownloadProgress(percent);
       },
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'license.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      window.URL.revokeObjectURL(downloadUrl);

      setMessage({ text: "License downloaded successfully!", type: "success" });
    } catch (error) {
      setMessage({ text: "Failed to download the license. Please try again later.", type: "error" });
    } finally {
    setDownloadProgress(null); 
   }
  };

  const handleDownloadTrack = async () => {
  if (!downloadUrl) {
    setMessage({ text: "Download URL is not available.", type: "error" });
    return;
  }

  try {
    setDownloadProgress(0);
    const token = localStorage.getItem('authToken');

    const response = await axios.get(downloadUrl, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      onDownloadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setDownloadProgress(percent);
      },
    });

    let filename = '';
    const disposition = response.headers['content-disposition'];
    if (disposition && disposition.includes('filename=')) {
      const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch != null && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    } else {
      const urlParts = downloadUrl.split('/');
      filename = urlParts[urlParts.length - 1];
    }

    if (!filename) {
      filename = 'Beat';
    }

    const blob = new Blob([response.data]);
    const downloadUrlBlob = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrlBlob;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(downloadUrlBlob);

    setMessage({ text: "Beat downloaded successfully!", type: "success" });
  } catch {
    setMessage({ text: "Failed to download the beat. Please try again later.", type: "error" });
  } finally {
    setDownloadProgress(null);
  }
};

  if (loading) {
    return (
      <div className={styles.afterPaymentContainer}>
        <div className={styles.afterPaymentContent}>Loading payment status...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={styles.afterPaymentContainer}>
        <div className={styles.afterPaymentContent}>
          <h2>Please Log In</h2>
          <p>You need to be logged in to access this page.</p>
        </div>
      </div>
    );
  }

  let content;

  switch (currentStatus) {
    case "success":
      content = (
        <div className={styles.success}>
          <h2>Thanks for your payment!</h2>
          <p>Your payment went through smoothly!</p>
          <p>You can cook up and grab your beat and license anytime in 'Your Own Stuff'!</p>

          {!licenseGenerated ? (
            <div className={styles.actionsContainer}>
              <div className={styles.inputContainer}>
                <input
                  type="text"
                  placeholder="Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className={styles.inputField}
                />
              </div>
              <div className={styles.buttonsContainer}>
                <button onClick={handleGenerateLicense} className={styles.generateButton}>
                  Generate License
                </button>
                {downloadUrl && (
                  <button onClick={handleDownloadTrack} className={styles.downloadButton}>
                    Download Beat
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.buttonsContainer}>
              {licenseUrl && (
                <button onClick={handleDownloadLicense} className={styles.downloadButton}>
                  Download License
                </button>
              )}
              {downloadUrl && (
                <button onClick={handleDownloadTrack} className={styles.downloadButton}>
                  Download Beat
                </button>
              )}
            </div>
          )}
        </div>
      );
      break;

    case "failed":
      content = (
        <div className={`${styles.failed}`}>
          <h2>Payment Failed</h2>
          <p>Something went sideways. Please try again.</p>
        </div>
      );
      break;
    case "error":
      content = (
        <div className={`${styles.error}`}>
          <h2>Payment Error</h2>
          <p>We hit a snag processing your payment.</p>
        </div>
      );
      break;
    default:
      content = (
        <div className={`${styles.unknown}`}>
          <h2>Unknown Payment Status</h2>
          <p>
            We couldn't figure out your payment status. Please reach out to support.
          </p>
        </div>
      );
  }

  return (
    <div className={styles.afterPaymentContainer}>
      <img
        src={backIcon}
        alt="Back"
        className={`${styles.backIcon} ${styles.visible}`}
        onClick={onBack}
      />
      <div className={styles.afterPaymentContent}>
        {content}
      </div>
      {message && (
        <div className={styles.overlay}>
          <div className={`${styles.overlayMessage} ${styles[messageType]}`}>
            {message.text}
          </div>
        </div>
      )}
      {downloadProgress !== null && (
      <div className={styles.progressOverlay}>
        <div className={styles.progressText}>{downloadProgress}%</div>
      </div>
    )}
    </div>
  );
};

export default AfterPayment;

